import { giftRecipientsAtom } from '@activities/gift/GiftOrderActivity/atoms/giftOrderAtoms';
import { toPhone } from '@constants/regexp';
import useUser from '@hooks/auth/useUser';
import { parseGiftOrderRequest } from '@utils/restoreGiftOrderUtils';
import { useAtom } from 'jotai';
import {
    ChangeEvent, ReactNode, useEffect, useMemo, useState,
} from 'react';

import GiftOrderContext, { GiftFormProps } from './GiftOrderContext';

interface Props {
    children: ReactNode;
    originalRequest?: string;
}

function GiftOrderProvider({ children, originalRequest }: Props) {
    const { user } = useUser();
    const restored = useMemo(() => parseGiftOrderRequest(originalRequest), [originalRequest]);
    const [giftRecipients, setGiftRecipients] = useAtom(giftRecipientsAtom);
    const [fields, setFields] = useState({
        userName: restored?.userName,
        message: restored?.message ?? '',
        cardName: restored?.cardName,
    });

    useEffect(() => {
        setGiftRecipients(restored?.giftRecipients ?? []);
    }, [setGiftRecipients, restored]);

    useEffect(() => {
        const identityName = user?.identityInfo?.name;
        if (identityName) setFields((prev) => ({ ...prev, userName: identityName }));
    }, [user?.identityInfo?.name]);

    const state: GiftFormProps = {
        ...fields,
        userPhone: toPhone(user?.phoneNumber || restored?.userPhone || ''),
        userName: fields.userName ?? user?.identityInfo?.name ?? user?.nickname ?? '',
        giftRecipients,
    };

    const enabled = useMemo(() => (
        !!state.userName
        && !!state.userPhone
        && giftRecipients.length > 0
    ), [state.userName, state.userPhone, giftRecipients.length]);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.currentTarget;
        setFields((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const setState = (value: React.SetStateAction<GiftFormProps>) => {
        const next = typeof value === 'function' ? value(state) : value;
        setGiftRecipients(next.giftRecipients);
        setFields({
            userName: next.userName,
            message: next.message ?? '',
            cardName: next.cardName,
        });
    };

    return (
        <GiftOrderContext value={{
            state,
            setState,
            handleChange,
            enabled,
        }}>
            {children}
        </GiftOrderContext>
    );
}

export default GiftOrderProvider;
